import { Directive, EventEmitter, HostListener, Input, OnDestroy, Output } from "@angular/core";
import { Observable } from "rxjs";
import { finalize, take } from "rxjs/operators";
import { SubSink } from "subsink";

/**
 * Generic "load on hover" hook for tooltips.
 *
 * Attach to any element that hosts a tooltip (e.g. `<app-tool-tip>`). On the first
 * mouseenter it invokes the provided loader, then emits the resolved text so the
 * host can update the tooltip content lazily. The loader is called at most once.
 *
 * Usage:
 *   <app-tool-tip [text]="model.tooltip"
 *                 [app-lazy-tooltip]="model.tooltipLoader"
 *                 (lazyTooltipLoaded)="model.tooltip = $event"></app-tool-tip>
 */
@Directive({
  selector: "[app-lazy-tooltip]",
})
export class LazyTooltipDirective implements OnDestroy {
  private sink = new SubSink();
  private isLoading = false;

  // tslint:disable-next-line:no-input-rename — alias matches the directive selector
  @Input("app-lazy-tooltip") loader: () => Observable<string>;
  @Output() lazyTooltipLoaded = new EventEmitter<string>();

  @HostListener("mouseenter")
  onMouseEnter(): void {
    // Re-fetches on every hover so the content stays fresh; the isLoading guard
    // prevents overlapping calls from rapid/repeated hovers.
    if (this.isLoading || typeof this.loader !== "function") {
      return;
    }

    this.isLoading = true;
    this.sink.add(
      this.loader()
        .pipe(
          take(1),
          finalize(() => (this.isLoading = false))
        )
        .subscribe(text => this.lazyTooltipLoaded.emit(text))
    );
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }
}
